import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { SortSelect } from '../components/SortSelect';
import { ExportDialog } from '../components/ExportDialog';
import { useExport } from '../hooks/useExport';
import { exportTableToPDF } from '../utils/html2pdfExport';
import { FilterPanel } from '../components/FilterPanel';
import EmlakDetailDialog from '../components/EmlakDetailDialog';
import EmlakExpiryBanner from '../components/EmlakExpiryBanner';
import Pagination from '../components/Pagination';
import { useServerList } from '../hooks/useServerList';
import { AdresKonumSecici } from '../components/maps/AdresKonumSecici';
import { TesisTuruSecici } from '../components/TesisTuruSecici';
import { TESIS_TURU_OPTIONS, tesisTuruText, tesisTuruList } from '../utils/tesisTuru';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMLAK_EXPORT_COLUMNS = [
  { key: 'sno', label: 'S.NO' },
  { key: 'tasinmaz_turu', label: 'Taşınmaz Türü' },
  { key: 'il', label: 'İl' },
  { key: 'ilce', label: 'İlçe' },
  { key: 'mahalle', label: 'Mahalle' },
  { key: 'pafta', label: 'Pafta' },
  { key: 'ada', label: 'Ada' },
  { key: 'parsel', label: 'Parsel' },
  { key: 'mulkiyet', label: 'Mülkiyet' },
  { key: 'belediye', label: 'Belediye' },
  { key: 'kamu_kurum_adi', label: 'Kamu Kurum Adı' },
  { key: 'alan_m2', label: 'Alan (M²)' },
  { key: 'tahsis_verildi_mi', label: 'Tahsis Verildi Mi' },
  { key: 'tahsis_edilen_kurum', label: 'Tahsis Edilen Kurum' },
  { key: 'tahsis_baslangic_tarihi', label: 'Tahsis Başlangıç' },
  { key: 'tahsis_bitis_tarihi', label: 'Tahsis Bitiş' },
  { key: 'kiraya_verilen_kurum', label: 'Kiraya Verilen Kurum' },
  { key: 'kira_baslangic_tarihi', label: 'Kira Başlangıç' },
  { key: 'kira_bitis_tarihi', label: 'Kira Bitiş' },
  { key: 'kurum_tahsis_baslangic_tarihi', label: 'Kurum Tahsis Başlangıç' },
  { key: 'kurum_tahsis_bitis_tarihi', label: 'Kurum Tahsis Bitiş' },
  { key: 'tesis_turu', label: 'Tesis Türü' },
  { key: 'tesis_adi', label: 'Tesis Adı' },
  { key: 'yapim_yili', label: 'Yapım Yılı' },
  { key: 'insaat_alani_m2', label: 'İnşaat Alanı (M²)' },
  { key: 'cati_alani_m2', label: 'Çatı Alanı (M²)' },
  { key: 'deprem_analizi_yapildi_mi', label: 'Deprem Analizi' },
  { key: 'analiz_yapim_tarihi', label: 'Analiz Yapım Tarihi' },
  { key: 'hangi_yonetmelik', label: 'Hangi Yönetmelik' },
  { key: 'guclendirme_ihtiyaci', label: 'Güçlendirme İhtiyacı' },
  { key: 'guclendirme_tarihi', label: 'Güçlendirme Tarihi' },
  { key: 'yangin_yonetmeligi_uygunlugu', label: 'Yangın Yönetmeliği' },
  { key: 'yangin_uygun_tarih', label: 'Yangın Uygun Tarih' },
  { key: 'yikim_tarihi', label: 'Yıkım Tarihi' },
  { key: 'yikim_notu', label: 'Yıkım Notu' },
  { key: 'enlem', label: 'Enlem' },
  { key: 'boylam', label: 'Boylam' },
  { key: 'aciklama', label: 'Açıklama' },
];

const ISTANBUL_ILCELERI = [
  'ADALAR', 'ARNAVUTKÖY', 'ATAŞEHİR', 'AVCILAR', 'BAĞCILAR', 'BAHÇELİEVLER',
  'BAKIRKÖY', 'BAŞAKŞEHİR', 'BAYRAMPAŞA', 'BEŞİKTAŞ', 'BEYKOZ', 'BEYLİKDÜZÜ',
  'BEYOĞLU', 'BÜYÜKÇEKMECE', 'ÇATALCA', 'ÇEKMEKÖY', 'ESENLER', 'ESENYURT',
  'EYÜPSULTAN', 'FATİH', 'GAZİOSMANPAŞA', 'GÜNGÖREN', 'KADIKÖY', 'KAĞITHANE',
  'KARTAL', 'KÜÇÜKÇEKMECE', 'MALTEPE', 'PENDİK', 'SANCAKTEPE', 'SARIYER',
  'SİLİVRİ', 'SULTANBEYLİ', 'SULTANGAZİ', 'ŞİLE', 'ŞİŞLİ', 'TUZLA',
  'ÜMRANİYE', 'ÜSKÜDAR', 'ZEYTİNBURNU'
];

const EMPTY_EMLAK_FORM = {
  tasinmaz_turu: '',
  il: 'İSTANBUL',
  ilce: '',
  mahalle: '',
  pafta: '',
  ada: '',
  parsel: '',
  parseller: [],
  mulkiyet: '',
  belediye: '',
  diger_mulkiyet_aciklama: '',
  kamu_kurum_adi: '',
  tahsis_verildi_mi: '',
  tahsis_edilen_kurum: '',
  kiraya_verilen_kurum: '',
  kira_baslangic_tarihi: '',
  kira_bitis_tarihi: '',
  alan_m2: '',
  tahsis_baslangic_tarihi: '',
  tahsis_bitis_tarihi: '',
  kurum_tahsis_baslangic_tarihi: '',
  kurum_tahsis_bitis_tarihi: '',
  tesis_turu: [],
  alt_tesis_turu: '',
  diger_tesis_turu: '',
  tesis_adi: '',
  yapim_yili: '',
  insaat_alani_m2: '',
  cati_alani_m2: '',
  deprem_analizi_yapildi_mi: '',
  analiz_yapim_tarihi: '',
  hangi_yonetmelik: '',
  guclendirme_ihtiyaci: '',
  guclendirme_tarihi: '',
  yangin_yonetmeligi_uygunlugu: '',
  yangin_uygun_tarih: '',
  yikim_tarihi: '',
  yikim_notu: '',
  enlem: '',
  boylam: '',
  aciklama: '',
  kamulastirma_tarihi: '',
  deger_tespit_sirketi: '',
  deger_tespit_bedeli: '',
  komisyon_tespit_bedeli: '',
  uzlasma_bedeli: '',
  kira_bedeli_kdv_dahil: '',
};

// Kayıttaki TÜM alanları düzenleme formuna yükler (eksik alan = eski değerin formda görünmemesi)
const emlakToForm = (emlak) => {
  const form = { ...EMPTY_EMLAK_FORM };
  Object.keys(EMPTY_EMLAK_FORM).forEach((key) => {
    if (emlak[key] !== undefined && emlak[key] !== null) form[key] = emlak[key];
  });
  form.tesis_turu = tesisTuruList(emlak.tesis_turu);
  form.parseller = Array.isArray(emlak.parseller) ? emlak.parseller : [];
  return form;
};

const EmlakIslemleriPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState([]);
  // UI filtre state'i (FilterPanel doğrudan setFilters çağırır)
  const [uiFilters, setUiFilters] = useState({
    isin_adi: '',
    tasinmaz_turu: [],
    il: [],
    ilce: [],
    mahalle: '',
    mulkiyet: [],
    tahsis_verildi_mi: [],
    tesis_turu: [],
    deprem_analizi_yapildi_mi: [],
    guclendirme_ihtiyaci: [],
    yangin_yonetmeligi_uygunlugu: [],
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sortKey, setSortKey] = useState('kayit_sirasi');
  const [editMode, setEditMode] = useState(false);
  const [editingEmlak, setEditingEmlak] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exportScope, setExportScope] = useState('filtered'); // 'page' | 'filtered' | 'selected'
  const [exporting, setExporting] = useState(false);
  const [detailEmlak, setDetailEmlak] = useState(null);
  const { selectedColumns, setSelectedColumns, pdfPageSize, setPdfPageSize, toggleColumn } = useExport('a4', EMLAK_EXPORT_COLUMNS.map(c => c.key));

  // UI sort key'i backend sort string'ine çevir
  const SORT_MAP = {
    kayit_sirasi: 'created_at:desc',
    ilce_az: 'ilce:asc', ilce_za: 'ilce:desc',
    tesis_adi_az: 'tesis_adi:asc', tesis_adi_za: 'tesis_adi:desc',
    tesis_turu_az: 'tesis_turu:asc',
    alan_buyuk: 'alan_m2:desc', alan_kucuk: 'alan_m2:asc',
    tahsis_bas_artan: 'tahsis_baslangic_tarihi:asc', tahsis_bas_azalan: 'tahsis_baslangic_tarihi:desc',
    tahsis_bitis_artan: 'tahsis_bitis_tarihi:asc', tahsis_bitis_azalan: 'tahsis_bitis_tarihi:desc',
    kurum_tahsis_bas_artan: 'kurum_tahsis_baslangic_tarihi:asc', kurum_tahsis_bas_azalan: 'kurum_tahsis_baslangic_tarihi:desc',
    kurum_tahsis_bitis_artan: 'kurum_tahsis_bitis_tarihi:asc', kurum_tahsis_bitis_azalan: 'kurum_tahsis_bitis_tarihi:desc',
    kira_bas_artan: 'kira_baslangic_tarihi:asc', kira_bas_azalan: 'kira_baslangic_tarihi:desc',
    kira_bitis_artan: 'kira_bitis_tarihi:asc', kira_bitis_azalan: 'kira_bitis_tarihi:desc',
  };

  // useServerList hook'u
  const list = useServerList('/emlak-islemleri', {
    pageSize: 100,
    initialSort: 'created_at:desc',
    initialFilters: {},
  });

  // UI filtreyi backend filtrelerine senkronize et
  useEffect(() => {
    const f = {};
    // Multi-select filtreler doğrudan array olarak gönderilir, hook bunları csv yapar
    ['tasinmaz_turu', 'il', 'ilce', 'mulkiyet', 'tahsis_verildi_mi', 'tesis_turu',
     'deprem_analizi_yapildi_mi', 'guclendirme_ihtiyaci', 'yangin_yonetmeligi_uygunlugu'].forEach(k => {
      if (Array.isArray(uiFilters[k]) && uiFilters[k].length > 0) f[k] = uiFilters[k];
    });
    if (uiFilters.mahalle) f.mahalle = uiFilters.mahalle;
    list.setFilters(f);
    list.setSearch(uiFilters.isin_adi || '');
  }, [JSON.stringify(uiFilters)]);

  // Sort değişimini hook'a ilet
  useEffect(() => {
    list.setSort(SORT_MAP[sortKey] || 'created_at:desc');
  }, [sortKey]);

  const emlaklar = list.items;
  const loading = list.loading;

  // İlçe seçenekleri: server tarafından sınırlandığı için sayfa içeriğinden + bilinen listeden türetelim
  const ilceOptions = useMemo(() => {
    const set = new Set(ISTANBUL_ILCELERI);
    emlaklar.forEach(e => e.ilce && set.add(e.ilce));
    return [...set].sort().map(i => ({ v: i, l: i }));
  }, [emlaklar]);

  const EXPORT_COLUMNS = EMLAK_EXPORT_COLUMNS;

  // Alan formatı - bölüklerine göre ayır
  const formatArea = (value) => {
    if (!value && value !== 0) return '-';
    const num = parseFloat(String(value).replace(/[^0-9.,]/g, '').replace(',', '.'));
    if (isNaN(num)) return value;
    return num.toLocaleString('tr-TR');
  };

  // Mülkiyet değerini okunabilir metne çevir
  const getMulkiyetText = (emlak) => {
    const map = {
      'milli_emlak': 'MİLLİ EMLAK',
      'ogm': 'OGM',
      'belediye': `BELEDİYE${emlak.belediye ? ` (${emlak.belediye})` : ''}`,
      'il_mudurlugu': 'İL MÜDÜRLÜĞÜ',
      'il_mudurlugu_kamulastirma': 'İL MÜDÜRLÜĞÜ (KAMULAŞTIRMA)',
      'kamu_kurum': 'KAMU KURUM',
      'diger': `DİĞER${emlak.diger_mulkiyet_aciklama ? ` (${emlak.diger_mulkiyet_aciklama})` : ''}`,
    };
    return map[emlak.mulkiyet] || emlak.mulkiyet || '-';
  };

  // Export veri satırı oluştur
  const buildExportRow = (emlak, idx) => {
    const row = {};
    row['sno'] = String(idx + 1);
    row['tasinmaz_turu'] = emlak.tasinmaz_turu === 'arsa' ? 'Arsa'
      : emlak.tasinmaz_turu === 'yapi_yikildi' ? 'Yapı (YIKILDI)'
      : 'Yapı';
    row['il'] = emlak.il || '-';
    row['ilce'] = emlak.ilce || '-';
    row['mahalle'] = emlak.mahalle || '-';

    // Ek parseller varsa her sütuna ekle: "12 + Ek: 27, 28"
    const ekList = Array.isArray(emlak.parseller) ? emlak.parseller : [];
    const buildKadastroCell = (mainValue, key) => {
      const main = mainValue || '-';
      const ekVals = ekList.map(p => (p?.[key] || '').toString().trim()).filter(Boolean);
      if (ekVals.length === 0) return main;
      return `${main}\n+ Ek: ${ekVals.join(', ')}`;
    };
    row['pafta'] = buildKadastroCell(emlak.pafta, 'pafta');
    row['ada'] = buildKadastroCell(emlak.ada, 'ada');
    row['parsel'] = buildKadastroCell(emlak.parsel, 'parsel');

    row['mulkiyet'] = getMulkiyetText(emlak);
    row['belediye'] = emlak.belediye || '-';
    row['kamu_kurum_adi'] = emlak.kamu_kurum_adi || '-';
    row['alan_m2'] = formatArea(emlak.alan_m2);
    const TAHSIS_DURUM_TR = { 'hayir': 'Hayır', 'tahsis_verildi': 'Tahsis Verildi', 'kiraya_verildi': 'Kiraya Verildi', 'kiralandi': 'Kiralandı', 'evet': 'Evet' };
    row['tahsis_verildi_mi'] = TAHSIS_DURUM_TR[emlak.tahsis_verildi_mi] || emlak.tahsis_verildi_mi || '-';
    row['tahsis_edilen_kurum'] = emlak.tahsis_edilen_kurum || '-';
    row['tahsis_baslangic_tarihi'] = emlak.tahsis_baslangic_tarihi || '-';
    row['tahsis_bitis_tarihi'] = emlak.tahsis_bitis_tarihi || '-';
    row['kiraya_verilen_kurum'] = emlak.kiraya_verilen_kurum || '-';
    row['kira_baslangic_tarihi'] = emlak.kira_baslangic_tarihi || '-';
    row['kira_bitis_tarihi'] = emlak.kira_bitis_tarihi || '-';
    row['kurum_tahsis_baslangic_tarihi'] = emlak.kurum_tahsis_baslangic_tarihi || '-';
    row['kurum_tahsis_bitis_tarihi'] = emlak.kurum_tahsis_bitis_tarihi || '-';
    row['tesis_turu'] = tesisTuruText(emlak.tesis_turu) || '-';
    row['tesis_adi'] = emlak.tesis_adi || '-';
    row['yapim_yili'] = emlak.yapim_yili || '-';
    row['insaat_alani_m2'] = formatArea(emlak.insaat_alani_m2);
    row['cati_alani_m2'] = formatArea(emlak.cati_alani_m2);
    row['deprem_analizi_yapildi_mi'] = emlak.deprem_analizi_yapildi_mi || '-';
    row['analiz_yapim_tarihi'] = emlak.analiz_yapim_tarihi || '-';
    row['hangi_yonetmelik'] = emlak.hangi_yonetmelik || '-';
    row['guclendirme_ihtiyaci'] = emlak.guclendirme_ihtiyaci || '-';
    row['guclendirme_tarihi'] = emlak.guclendirme_tarihi || '-';
    row['yangin_yonetmeligi_uygunlugu'] = emlak.yangin_yonetmeligi_uygunlugu || '-';
    row['yangin_uygun_tarih'] = emlak.yangin_uygun_tarih || '-';
    row['yikim_tarihi'] = emlak.yikim_tarihi || '-';
    row['yikim_notu'] = emlak.yikim_notu || '-';
    row['enlem'] = emlak.enlem || '-';
    row['boylam'] = emlak.boylam || '-';
    row['aciklama'] = emlak.aciklama || '-';
    return row;
  };

  // Export işlemi - scope: 'page' (görünen sayfa) | 'filtered' (tüm filtrelenmiş) | 'selected' (işaretli)
  const handleExport = async () => {
    if (selectedColumns.length === 0) {
      alert('En az bir sütun seçmelisiniz.');
      return;
    }
    setExporting(true);
    try {
      let rowsToExport = [];
      if (exportScope === 'selected') {
        rowsToExport = emlaklar.filter(e => selectedRows.includes(e.id));
      } else if (exportScope === 'page') {
        rowsToExport = emlaklar; // Mevcut sayfa
      } else {
        // 'filtered' → server'dan tüm eşleşen kayıtları getir
        rowsToExport = await list.fetchAll();
      }
      const headers = selectedColumns.map(key => EXPORT_COLUMNS.find(c => c.key === key)?.label || key);
      const data = rowsToExport.map((emlak, idx) => {
        const fullRow = buildExportRow(emlak, idx);
        return selectedColumns.map(key => fullRow[key] || '-');
      });

      const titleSuffix = exportScope === 'selected'
        ? 'Seçili Kayıtlar'
        : exportScope === 'page' ? 'Görünen Sayfa' : 'Tüm Filtrelenmiş Kayıtlar';

      if (exportFormat === 'pdf') {
        await exportTableToPDF(
          headers,
          data,
          `Emlak İşlemleri - ${titleSuffix}`,
          `emlak_islemleri_${exportScope}.pdf`,
          { pageSize: pdfPageSize }
        );
      } else {
        const wsData = [headers, ...data];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 2, 14) }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Emlak İşlemleri');
        XLSX.writeFile(wb, `emlak_islemleri_${exportScope}.xlsx`);
      }
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Export error:', error);
      alert('Export sırasında hata oluştu: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const [formData, setFormData] = useState({ ...EMPTY_EMLAK_FORM });

  useEffect(() => {
    if (!user?.is_admin && !user?.has_emlak_kamulastirma_access) {
      navigate('/dashboard');
    }
  }, []);

  const fetchEmlaklar = () => list.refetch();

  const canEdit = !user?.is_viewer && (user?.is_admin || user?.has_emlak_kamulastirma_access);

  const openEditDialog = (emlak) => {
    setEditMode(true);
    setEditingEmlak(emlak);
    setFormData(emlakToForm(emlak));
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editMode && editingEmlak) {
        await axios.put(`${API}/emlak-islemleri/${editingEmlak.id}`, formData, { withCredentials: true });
        alert('Taşınmaz güncellendi!');
      } else {
        await axios.post(`${API}/emlak-islemleri`, formData, { withCredentials: true });
        alert('Taşınmaz başarıyla kaydedildi!');
      }
      setDialogOpen(false);
      resetForm();
      setEditMode(false);
      setEditingEmlak(null);
      fetchEmlaklar();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Bilinmeyen hata';
      alert('Hata: ' + errorMsg);
    }
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_EMLAK_FORM });
    setCurrentStep(1);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Header */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="/logo.png" 
                alt="Logo" 
                className="w-12 h-12 object-contain cursor-pointer" 
                onClick={() => navigate('/dashboard')} 
              />
              <div>
                <h1 className="text-xl font-bold text-white">Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu</h1>
                <p className="text-xs text-gray-400">Emlak İşlemleri</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-gray-300 hover:text-white" data-testid="anasayfa-nav-button">
                Anasayfa
              </Button>
              <Button onClick={() => navigate('/emlak-kamulastirma')} variant="ghost" className="text-gray-300 hover:text-white">
                Geri Dön
              </Button>
              <Button onClick={handleLogout} variant="ghost" className="text-red-400 hover:text-red-300">
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">🏠 Emlak İşlemleri</h2>
            <p className="text-gray-400">Bina ve arsa kayıtlarını yönetin ({list.total} kayıt)</p>
          </div>
          <div className="flex gap-3">
            {selectedRows.length > 0 ? (
              <>
                <Button
                  data-testid="export-selected-btn"
                  onClick={() => { setExportScope('selected'); setExportDialogOpen(true); }}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Seçilileri Yazdır ({selectedRows.length})
                </Button>
                {user?.is_admin && (
                  <Button
                    data-testid="delete-selected-btn"
                    onClick={async () => {
                      if (window.confirm(`${selectedRows.length} taşınmazı silmek istediğinizden emin misiniz?`)) {
                        try {
                          await Promise.all(selectedRows.map(id =>
                            axios.delete(`${API}/emlak-islemleri/${id}`, { withCredentials: true })
                          ));
                          alert('Seçili taşınmazlar silindi');
                          setSelectedRows([]);
                          fetchEmlaklar();
                        } catch (error) {
                          alert('Hata: ' + (error.response?.data?.detail || error.message));
                        }
                      }
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Seçilileri Sil ({selectedRows.length})
                  </Button>
                )}
              </>
            ) : (
              list.total > 0 && (
                <div className="flex gap-2">
                  <Button
                    data-testid="export-page-btn"
                    onClick={() => { setExportScope('page'); setExportDialogOpen(true); }}
                    className="bg-purple-600/80 hover:bg-purple-700 text-white"
                    title="Görünen sayfayı yazdır"
                  >
                    Sayfayı Yazdır ({emlaklar.length})
                  </Button>
                  <Button
                    data-testid="export-all-btn"
                    onClick={() => { setExportScope('filtered'); setExportDialogOpen(true); }}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    title="Tüm filtrelenmiş kayıtları yazdır"
                  >
                    Tümünü Yazdır ({list.total})
                  </Button>
                </div>
              )
            )}
            <Button
              onClick={() => {
                setEditMode(false);
                setEditingEmlak(null);
                resetForm();
                setDialogOpen(true);
              }}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Taşınmaz Ekle
            </Button>
          </div>
        </div>

        {/* Tahsis/Kira Bitiş Hatırlatma Bandı */}
        <EmlakExpiryBanner
          refreshKey={list.total}
          onSelectEmlak={(emlakId) => {
            const emlak = emlaklar.find(e => e.id === emlakId);
            if (emlak) setDetailEmlak(emlak);
          }}
        />

        {/* Filtreler */}
        <FilterPanel
          filters={uiFilters}
          setFilters={setUiFilters}
          totalCount={list.total}
          filteredCount={list.total}
          resetFilters={() => setUiFilters({ isin_adi: '', tasinmaz_turu: [], il: [], ilce: [], mahalle: '', mulkiyet: [], tahsis_verildi_mi: [], tesis_turu: [], deprem_analizi_yapildi_mi: [], guclendirme_ihtiyaci: [], yangin_yonetmeligi_uygunlugu: [] })}
          filterConfig={[
            { key: 'isin_adi', label: 'Ara (açıklama, mahalle, ada, parsel...)', type: 'text' },
            { key: 'mahalle', label: 'Mahalle', type: 'text' },
            { key: 'tasinmaz_turu', label: 'Taşınmaz Türü', type: 'multi', options: [{v:'arsa',l:'Arsa'},{v:'yapi',l:'Yapı'},{v:'yapi_yikildi',l:'Yapı (YIKILDI)'}] },
            { key: 'ilce', label: 'İlçe', type: 'multi', options: ilceOptions },
            { key: 'mulkiyet', label: 'Mülkiyet', type: 'multi', options: [{v:'milli_emlak',l:'MİLLİ EMLAK'},{v:'ogm',l:'OGM'},{v:'belediye',l:'BELEDİYE'},{v:'il_mudurlugu',l:'İL MÜDÜRLÜĞÜ'},{v:'il_mudurlugu_kamulastirma',l:'İL MÜDÜRLÜĞÜ (KAMULAŞTIRMA)'},{v:'kamu_kurum',l:'KAMU KURUM'},{v:'diger',l:'DİĞER'}] },
            { key: 'tahsis_verildi_mi', label: 'Tahsis/Kira Durumu', type: 'multi', options: [{v:'hayir',l:'Hayır'},{v:'tahsis_verildi',l:'Tahsis Verildi'},{v:'kiraya_verildi',l:'Kiraya Verildi'},{v:'kiralandi',l:'Kiralandı'},{v:'evet',l:'Evet (Eski)'}] },
            { key: 'tesis_turu', label: 'Tesis Türü (Yapı)', type: 'multi', options: TESIS_TURU_OPTIONS.map(o => ({ v: o.value, l: o.label })) },
            { key: 'deprem_analizi_yapildi_mi', label: 'Deprem Analizi', type: 'multi', options: [{v:'evet',l:'Evet'},{v:'hayir',l:'Hayır'}] },
            { key: 'guclendirme_ihtiyaci', label: 'Güçlendirme İhtiyacı', type: 'multi', options: [{v:'var',l:'Var'},{v:'yok',l:'Yok'}] },
            { key: 'yangin_yonetmeligi_uygunlugu', label: 'Yangın Yönetmeliği', type: 'multi', options: [{v:'var',l:'Var'},{v:'yok',l:'Yok'}] },
          ]}
        />

        {/* Emlak Listesi */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">
                Kayıtlı Taşınmazlar ({list.total})
                {loading && <span className="ml-2 text-xs text-gray-500 animate-pulse">yükleniyor…</span>}
              </CardTitle>
              <SortSelect value={sortKey} onChange={setSortKey} customOptions={[
                { value: 'kayit_sirasi', label: 'Kayıt Sırası' },
                { value: 'ilce_az', label: 'İlçe (A-Z)' },
                { value: 'ilce_za', label: 'İlçe (Z-A)' },
                { value: 'tesis_adi_az', label: 'Tesis Adı (A-Z)' },
                { value: 'tesis_adi_za', label: 'Tesis Adı (Z-A)' },
                { value: 'tesis_turu_az', label: 'Tesis Türü (A-Z)' },
                { value: 'alan_buyuk', label: 'Alan (Büyük→Küçük)' },
                { value: 'alan_kucuk', label: 'Alan (Küçük→Büyük)' },
                { value: 'tahsis_bas_artan', label: 'Tahsis Başlangıç (Artan)' },
                { value: 'tahsis_bas_azalan', label: 'Tahsis Başlangıç (Azalan)' },
                { value: 'tahsis_bitis_artan', label: 'Tahsis Bitiş (Artan)' },
                { value: 'tahsis_bitis_azalan', label: 'Tahsis Bitiş (Azalan)' },
                { value: 'kurum_tahsis_bas_artan', label: 'Kurum Tahsis Başlangıç (Artan)' },
                { value: 'kurum_tahsis_bas_azalan', label: 'Kurum Tahsis Başlangıç (Azalan)' },
                { value: 'kurum_tahsis_bitis_artan', label: 'Kurum Tahsis Bitiş (Artan)' },
                { value: 'kurum_tahsis_bitis_azalan', label: 'Kurum Tahsis Bitiş (Azalan)' },
                { value: 'kira_bas_artan', label: 'Kira Başlangıç (Artan)' },
                { value: 'kira_bas_azalan', label: 'Kira Başlangıç (Azalan)' },
                { value: 'kira_bitis_artan', label: 'Kira Bitiş (Artan)' },
                { value: 'kira_bitis_azalan', label: 'Kira Bitiş (Azalan)' }
              ]} />
            </div>
          </CardHeader>
          <CardContent>
            {list.total === 0 ? (
              <p className="text-gray-400 text-center py-12">
                {loading ? 'Yükleniyor...' : 'Henüz taşınmaz kaydı bulunmamaktadır. "Taşınmaz Ekle" butonuna tıklayarak yeni kayıt oluşturabilirsiniz.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-800 text-gray-300">
                    <tr>
                      <th className="px-2 py-3">
                        <input
                          type="checkbox"
                          checked={selectedRows.length === emlaklar.length && emlaklar.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRows(emlaklar.map(em => em.id));
                            } else {
                              setSelectedRows([]);
                            }
                          }}
                          className="w-4 h-4"
                        />
                      </th>
                      <th className="px-2 py-3 text-left">S.NO</th>
                      <th className="px-4 py-3 text-left">Tür</th>
                      <th className="px-4 py-3 text-left">Tesis Türü</th>
                      <th className="px-4 py-3 text-left">Tesis Adı</th>
                      <th className="px-4 py-3 text-left">Konum</th>
                      <th className="px-4 py-3 text-left">Kadastro</th>
                      <th className="px-4 py-3 text-left">Mülkiyet</th>
                      <th className="px-4 py-3 text-left">Alan (M²)</th>
                      {user?.is_admin && <th className="px-4 py-3 text-left">Son Düzenleyen</th>}
                      <th className="px-4 py-3 text-left">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    {emlaklar.map((emlak, idx) => (
                      <tr key={emlak.id} className="border-b border-gray-800 hover:bg-gray-800/50 cursor-pointer" onClick={(e) => {
                        // Don't open detail if clicking on checkbox, button, or action cells
                        if (e.target.closest('input[type="checkbox"]') || e.target.closest('button')) return;
                        setDetailEmlak(emlak);
                      }} data-testid={`emlak-row-${emlak.id}`}>
                        <td className="px-2 py-3">
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(emlak.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedRows([...selectedRows, emlak.id]);
                              } else {
                                setSelectedRows(selectedRows.filter(id => id !== emlak.id));
                              }
                            }}
                            className="w-4 h-4"
                          />
                        </td>
                        <td className="px-2 py-3 text-gray-400">{(list.page - 1) * list.pageSize + idx + 1}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
                            emlak.tasinmaz_turu === 'arsa' ? 'bg-green-900 text-green-200'
                            : emlak.tasinmaz_turu === 'yapi_yikildi' ? 'bg-red-900 text-red-200'
                            : 'bg-blue-900 text-blue-200'
                          }`}>
                            {emlak.tasinmaz_turu === 'arsa' ? 'Arsa'
                              : emlak.tasinmaz_turu === 'yapi_yikildi' ? 'Yapı (YIKILDI)'
                              : 'Yapı'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm" data-testid={`emlak-row-tesis-turu-${emlak.id}`}>{tesisTuruText(emlak.tesis_turu) || '-'}</td>
                        <td className="px-4 py-3 text-sm">{emlak.tesis_adi || '-'}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <div className="font-medium">{emlak.ilce}</div>
                            <div className="text-xs text-gray-500">{emlak.mahalle}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs">
                            <div>Ada: {emlak.ada}</div>
                            <div>Parsel: {emlak.parsel}</div>
                            {emlak.pafta && <div>Pafta: {emlak.pafta}</div>}
                            {(emlak.parseller || []).length > 0 && (
                              <div className="mt-0.5 text-gray-500">+{emlak.parseller.length} ek parsel</div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs">
                            {getMulkiyetText(emlak)}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium">{formatArea(emlak.alan_m2)}</td>
                        {user?.is_admin && (
                          <td className="px-4 py-3">
                            {emlak.last_modified_by_name ? (
                              <div>
                                <p className="text-xs text-gray-300">{emlak.last_modified_by_name}</p>
                                <p className="text-xs text-gray-500">{emlak.last_modified_at ? new Date(emlak.last_modified_at).toLocaleString('tr-TR') : ''}</p>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-600">-</span>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {(canEdit || (!user?.is_viewer && emlak.created_by === user?.id)) && (
                              <Button
                                onClick={() => openEditDialog(emlak)}
                                variant="ghost"
                                size="sm"
                                className="text-blue-400 hover:text-blue-300"
                                data-testid={`emlak-duzenle-${emlak.id}`}
                              >
                                ✏️ Düzenle
                              </Button>
                            )}
                            {user?.is_admin && (
                              <Button
                                onClick={async () => {
                                  if (window.confirm('Bu taşınmazı silmek istediğinizden emin misiniz?')) {
                                    try {
                                      await axios.delete(`${API}/emlak-islemleri/${emlak.id}`, { withCredentials: true });
                                      alert('✅ Taşınmaz silindi');
                                      fetchEmlaklar();
                                    } catch (error) {
                                      alert('❌ Hata: ' + (error.response?.data?.detail || error.message));
                                    }
                                  }
                                }}
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300"
                              >
                                🗑️ Sil
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* Server-side pagination */}
            {list.total > 0 && (
              <div className="border-t border-gray-700/40 px-3 py-2">
                <Pagination
                  page={list.page}
                  totalPages={list.totalPages}
                  total={list.total}
                  pageSize={list.pageSize}
                  onPageChange={list.setPage}
                  onPageSizeChange={list.setPageSize}
                  loading={loading}
                  testIdPrefix="emlak-pagination"
                />
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Bina/Arsa Ekle Wizard Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-800">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl">{editMode ? 'Taşınmaz Düzenle' : 'Taşınmaz Ekle'}</DialogTitle>
            <DialogDescription className="text-gray-400">
              Adım {currentStep}/{['yapi', 'yapi_yikildi'].includes(formData.tasinmaz_turu) ? 5 : 3}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Adım 1 - Taşınmaz Türü Seçimi (her zaman göster) */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Taşınmaz Türü</Label>
                  <Select value={formData.tasinmaz_turu} onValueChange={(value) => setFormData({...formData, tasinmaz_turu: value})}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="tasinmaz-turu-select">
                      <SelectValue placeholder="Taşınmaz türü seçin" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      <SelectItem value="arsa" data-testid="tasinmaz-turu-arsa">Arsa</SelectItem>
                      <SelectItem value="yapi" data-testid="tasinmaz-turu-yapi">Yapı</SelectItem>
                      <SelectItem value="yapi_yikildi" data-testid="tasinmaz-turu-yapi-yikildi">Yapı (YIKILDI)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Yapı (YIKILDI) seçilince Yıkım Tarihi alanı */}
                {formData.tasinmaz_turu === 'yapi_yikildi' && (
                  <div className="p-3 bg-red-900/20 border border-red-700/40 rounded-lg space-y-2">
                    <p className="text-xs font-semibold text-red-400 flex items-center gap-1">
                      <span>⚠️</span> Yıkılmış Yapı Bilgisi
                    </p>
                    <div className="space-y-1">
                      <Label className="text-white text-xs">Yıkım Tarihi *</Label>
                      <Input
                        type="date"
                        value={formData.yikim_tarihi || ''}
                        onChange={(e) => setFormData({...formData, yikim_tarihi: e.target.value})}
                        className="bg-gray-800 border-gray-700 text-white"
                        data-testid="yikim-tarihi-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-white text-xs">Yıkım Nedeni / Notu</Label>
                      <Textarea
                        value={formData.yikim_notu || ''}
                        onChange={(e) => setFormData({...formData, yikim_notu: e.target.value})}
                        className="bg-gray-800 border-gray-700 text-white"
                        placeholder="Yıkım sebebi, kararname numarası vb."
                        rows={2}
                        data-testid="yikim-notu-textarea"
                      />
                    </div>
                    <p className="text-[11px] text-red-300/70">
                      Bu kayıt yapının yıkılmış olduğu durumdaki arşiv bilgilerini içerir. Devam eden adımlarda yıkılan yapının tüm bilgilerini girebilirsiniz.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Arsa - Adım 1: Konum Bilgileri */}
            {currentStep === 1 && formData.tasinmaz_turu === 'arsa' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Konum Bilgileri</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">İl *</Label>
                    <Input
                      value={formData.il}
                      onChange={(e) => setFormData({...formData, il: e.target.value})}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="İSTANBUL"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">İlçe</Label>
                    <Select value={formData.ilce} onValueChange={(value) => setFormData({...formData, ilce: value})}>
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                        <SelectValue placeholder="İlçe seçin" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-60">
                        {ISTANBUL_ILCELERI.map(ilce => (
                          <SelectItem key={ilce} value={ilce}>{ilce}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Mahalle</Label>
                  <Input
                    value={formData.mahalle}
                    onChange={(e) => setFormData({...formData, mahalle: e.target.value})}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="Mahalle adı"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Pafta</Label>
                    <Input
                      value={formData.pafta}
                      onChange={(e) => setFormData({...formData, pafta: e.target.value})}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="Örn: 12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Ada</Label>
                    <Input
                      value={formData.ada}
                      onChange={(e) => setFormData({...formData, ada: e.target.value})}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="Örn: 5" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Parsel</Label>
                    <Input
                      value={formData.parsel}
                      onChange={(e) => setFormData({...formData, parsel: e.target.value})}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="Örn: 6" />
                  </div>
                </div>
              </div>
            )}

            {/* Yapı - Adım 2: Konum ve Tesis Bilgileri */}
            {currentStep === 2 && ['yapi', 'yapi_yikildi'].includes(formData.tasinmaz_turu) && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Yapı Bilgileri</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">İlçe</Label>
                    <Select value={formData.ilce} onValueChange={(value) => setFormData({...formData, ilce: value})}>
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="İlçe seçin" /></SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-60">
                        {ISTANBUL_ILCELERI.map(ilce => (<SelectItem key={ilce} value={ilce}>{ilce}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Mahalle</Label>
                    <Input value={formData.mahalle} onChange={(e) => setFormData({...formData, mahalle: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Mahalle adı" />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Pafta</Label>
                    <Input value={formData.pafta} onChange={(e) => setFormData({...formData, pafta: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Pafta no" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Ada</Label>
                    <Input value={formData.ada} onChange={(e) => setFormData({...formData, ada: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Ada no" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Parsel</Label>
                    <Input value={formData.parsel} onChange={(e) => setFormData({...formData, parsel: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Parsel no" />
                  </div>
                </div>

                {/* Çoklu Pafta/Ada/Parsel */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-white text-sm">Ek Pafta / Ada / Parsel</Label>
                    <Button type="button" size="sm" variant="outline" className="h-7 text-xs border-gray-600 text-gray-300 hover:text-white"
                      onClick={() => setFormData({...formData, parseller: [...(formData.parseller || []), {pafta: '', ada: '', parsel: ''}]})}
                      data-testid="add-parsel-btn">
                      + Ekle
                    </Button>
                  </div>
                  {(formData.parseller || []).map((p, idx) => (
                    <div key={idx} className="grid grid-cols-4 gap-2 items-end">
                      <Input value={p.pafta} onChange={(e) => { const arr = [...formData.parseller]; arr[idx] = {...arr[idx], pafta: e.target.value}; setFormData({...formData, parseller: arr}); }} className="bg-gray-800 border-gray-700 text-white text-xs h-8" placeholder="Pafta" />
                      <Input value={p.ada} onChange={(e) => { const arr = [...formData.parseller]; arr[idx] = {...arr[idx], ada: e.target.value}; setFormData({...formData, parseller: arr}); }} className="bg-gray-800 border-gray-700 text-white text-xs h-8" placeholder="Ada" />
                      <Input value={p.parsel} onChange={(e) => { const arr = [...formData.parseller]; arr[idx] = {...arr[idx], parsel: e.target.value}; setFormData({...formData, parseller: arr}); }} className="bg-gray-800 border-gray-700 text-white text-xs h-8" placeholder="Parsel" />
                      <Button type="button" size="sm" variant="ghost" className="h-8 text-red-400 hover:text-red-300 px-2"
                        onClick={() => { const arr = formData.parseller.filter((_, i) => i !== idx); setFormData({...formData, parseller: arr}); }}>
                        Sil
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Koordinat Bilgileri (yalnızca Yapı & Yapı YIKILDI için) */}
                <AdresKonumSecici
                  enlem={formData.enlem}
                  boylam={formData.boylam}
                  onChange={({ enlem, boylam }) => setFormData({ ...formData, enlem, boylam })}
                />

                <div className="space-y-2">
                  <Label className="text-white">Tesis Türü (çoklu seçim)</Label>
                  <TesisTuruSecici
                    value={formData.tesis_turu}
                    onChange={(codes) => setFormData({...formData, tesis_turu: codes, diger_tesis_turu: codes.includes('diger') ? formData.diger_tesis_turu : '', alt_tesis_turu: codes.includes('spor_tesisi') ? formData.alt_tesis_turu : ''})}
                    testId="emlak-tesis-turu-secici"
                  />
                  {tesisTuruList(formData.tesis_turu).includes('spor_tesisi') && (
                    <div className="space-y-1 mt-2">
                      <Label className="text-gray-400 text-xs">Alt Tesis Türü</Label>
                      <Select value={formData.alt_tesis_turu || ''} onValueChange={(value) => setFormData({...formData, alt_tesis_turu: value})}>
                        <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Alt tesis türü seçin" /></SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700 text-white">
                          <SelectItem value="futbol_sahasi">Futbol Sahası</SelectItem>
                          <SelectItem value="voleybol_sahasi">Voleybol Sahası</SelectItem>
                          <SelectItem value="hentbol_sahasi">Hentbol Sahası</SelectItem>
                          <SelectItem value="basketbol_sahasi">Basketbol Sahası</SelectItem>
                          <SelectItem value="tenis_sahasi">Tenis Sahası</SelectItem>
                          <SelectItem value="atletizm_sahasi">Atletizm Sahası</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {tesisTuruList(formData.tesis_turu).includes('diger') && (
                    <Input value={formData.diger_tesis_turu || ''} onChange={(e) => setFormData({...formData, diger_tesis_turu: e.target.value})} className="bg-gray-800 border-gray-700 text-white mt-2" placeholder="Tesis türü adını girin..." />
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Tesis Adı</Label>
                  <Input value={formData.tesis_adi || ''} onChange={(e) => setFormData({...formData, tesis_adi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: Ataşehir Gençlik Merkezi" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Yapım Yılı</Label>
                    <Input type="number" value={formData.yapim_yili} onChange={(e) => setFormData({...formData, yapim_yili: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: 2005" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">İnşaat Alanı (m²)</Label>
                    <Input type="number" value={formData.insaat_alani_m2} onChange={(e) => setFormData({...formData, insaat_alani_m2: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: 2500" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Çatı Alanı (m²)</Label>
                  <Input type="number" value={formData.cati_alani_m2} onChange={(e) => setFormData({...formData, cati_alani_m2: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: 800" />
                </div>
              </div>
            )}

            {/* Yapı - Adım 4: Deprem Analizi */}
            {currentStep === 4 && ['yapi', 'yapi_yikildi'].includes(formData.tasinmaz_turu) && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Deprem ve Güçlendirme Bilgileri</h3>

                <div className="space-y-2">
                  <Label className="text-white">Deprem Performans Analizi Yapıldı mı?</Label>
                  <Select value={formData.deprem_analizi_yapildi_mi || 'none'} onValueChange={(value) => setFormData({...formData, deprem_analizi_yapildi_mi: value === 'none' ? '' : value, analiz_yapim_tarihi: value !== 'evet' ? '' : formData.analiz_yapim_tarihi, hangi_yonetmelik: value !== 'evet' ? '' : formData.hangi_yonetmelik, guclendirme_ihtiyaci: value !== 'evet' ? '' : formData.guclendirme_ihtiyaci, guclendirme_tarihi: value !== 'evet' ? '' : formData.guclendirme_tarihi})}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      <SelectItem value="none">Seçilmedi</SelectItem>
                      <SelectItem value="evet">Evet</SelectItem>
                      <SelectItem value="hayir">Hayır</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.deprem_analizi_yapildi_mi === 'evet' && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-white">Analiz Yapım Tarihi</Label>
                      <Input type="date" value={formData.analiz_yapim_tarihi} onChange={(e) => setFormData({...formData, analiz_yapim_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white">Hangi Yönetmelik</Label>
                      <Select value={formData.hangi_yonetmelik || 'none'} onValueChange={(value) => setFormData({...formData, hangi_yonetmelik: value === 'none' ? '' : value})}>
                        <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Yönetmelik seçin" /></SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700 text-white">
                          <SelectItem value="none">Seçilmedi</SelectItem>
                          <SelectItem value="1975">1975</SelectItem>
                          <SelectItem value="1998">1998</SelectItem>
                          <SelectItem value="2007">2007</SelectItem>
                          <SelectItem value="2018">2018</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white">Güçlendirme İhtiyacı</Label>
                      <Select value={formData.guclendirme_ihtiyaci || 'none'} onValueChange={(value) => setFormData({...formData, guclendirme_ihtiyaci: value === 'none' ? '' : value, guclendirme_tarihi: value !== 'var' ? '' : formData.guclendirme_tarihi})}>
                        <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Seçin" /></SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700 text-white">
                          <SelectItem value="none">Seçilmedi</SelectItem>
                          <SelectItem value="var">Var</SelectItem>
                          <SelectItem value="yok">Yok</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.guclendirme_ihtiyaci === 'var' && (
                      <div className="space-y-2">
                        <Label className="text-white">Güçlendirme Tarihi</Label>
                        <Input type="date" value={formData.guclendirme_tarihi} onChange={(e) => setFormData({...formData, guclendirme_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                      </div>
                    )}
                  </>
                )}

                {/* Yangın Yönetmeliği */}
                <div className="space-y-2">
                  <Label className="text-white">Binaların Yangından Korunması Hakkında Yönetmeliğe Uygunluğu</Label>
                  <Select value={formData.yangin_yonetmeligi_uygunlugu || 'none'} onValueChange={(value) => setFormData({...formData, yangin_yonetmeligi_uygunlugu: value === 'none' ? '' : value, yangin_uygun_tarih: value !== 'var' ? '' : formData.yangin_uygun_tarih})}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      <SelectItem value="none">Seçilmedi</SelectItem>
                      <SelectItem value="var">Var</SelectItem>
                      <SelectItem value="yok">Yok</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.yangin_yonetmeligi_uygunlugu === 'var' && (
                  <div className="space-y-2">
                    <Label className="text-white">Uygun Hale Getiriliş Tarihi</Label>
                    <Input type="date" value={formData.yangin_uygun_tarih} onChange={(e) => setFormData({...formData, yangin_uygun_tarih: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                  </div>
                )}

              </div>
            )}

            {/* Mülkiyet ve Alan: Arsa=2, Yapı=3 */}
            {((currentStep === 2 && formData.tasinmaz_turu === 'arsa') || (currentStep === 3 && ['yapi', 'yapi_yikildi'].includes(formData.tasinmaz_turu))) && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">📋 Mülkiyet & Tahsis Bilgileri</h3>
                
                <div className="space-y-2">
                  <Label className="text-white">Mülkiyet</Label>
                  <Select value={formData.mulkiyet} onValueChange={(value) => setFormData({...formData, mulkiyet: value})}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                      <SelectValue placeholder="Mülkiyet seçin" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      <SelectItem value="milli_emlak">MİLLİ EMLAK</SelectItem>
                      <SelectItem value="ogm">OGM</SelectItem>
                      <SelectItem value="belediye">BELEDİYE</SelectItem>
                      <SelectItem value="il_mudurlugu">İL MÜDÜRLÜĞÜ</SelectItem>
                      <SelectItem value="il_mudurlugu_kamulastirma">İL MÜDÜRLÜĞÜ (KAMULAŞTIRMA)</SelectItem>
                      <SelectItem value="kamu_kurum">KAMU KURUM VE KURULUŞLARI</SelectItem>
                      <SelectItem value="diger">DİĞER</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Belediye seçildiğinde belediye dropdown'ı göster */}
                {formData.mulkiyet === 'belediye' && (
                  <div className="space-y-2">
                    <Label className="text-white">Belediye</Label>
                    <Select value={formData.belediye} onValueChange={(value) => setFormData({...formData, belediye: value})}>
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                        <SelectValue placeholder="Belediye seçin" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-60">
                        <SelectItem value="istanbul_buyuksehir">İstanbul Büyükşehir Belediyesi</SelectItem>
                        {ISTANBUL_ILCELERI.map(ilce => (
                          <SelectItem key={ilce} value={ilce.toLowerCase()}>{ilce} BELEDİYESİ</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Kamu Kurum seçildiğinde kurum adı */}
                {formData.mulkiyet === 'kamu_kurum' && (
                  <div className="space-y-2">
                    <Label className="text-white">Kurum ve Kuruluş Adı</Label>
                    <Input value={formData.kamu_kurum_adi || ''} onChange={(e) => setFormData({...formData, kamu_kurum_adi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Kurum/Kuruluş adını girin" />
                  </div>
                )}

                {/* DİĞER seçildiğinde açıklama alanı göster */}
                {formData.mulkiyet === 'diger' && (
                  <div className="space-y-2">
                    <Label className="text-white">Mülkiyet Açıklaması</Label>
                    <Input
                      value={formData.diger_mulkiyet_aciklama || ''}
                      onChange={(e) => setFormData({...formData, diger_mulkiyet_aciklama: e.target.value})}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="Mülkiyet türünü belirtin (örn: Şahıs Mülkiyeti, Ortak Mülkiyet vb.)" />
                  </div>
                )}

                {/* Tahsis/Kira */}
                <div className="space-y-2">
                  <Label className="text-white">Tahsis/Kiraya Verildi mi/Kiralandı Mı?</Label>
                  <Select value={formData.tahsis_verildi_mi || 'none'} onValueChange={(value) => setFormData({...formData, tahsis_verildi_mi: value === 'none' ? '' : value, tahsis_edilen_kurum: '', kiraya_verilen_kurum: '', tahsis_baslangic_tarihi: '', tahsis_bitis_tarihi: '', kira_baslangic_tarihi: '', kira_bitis_tarihi: ''})}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                      <SelectValue placeholder="Seçin" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      <SelectItem value="none">Seçilmedi</SelectItem>
                      <SelectItem value="hayir">Hayır</SelectItem>
                      <SelectItem value="tahsis_verildi">Tahsis Verildi</SelectItem>
                      <SelectItem value="kiraya_verildi">Kiraya Verildi</SelectItem>
                      <SelectItem value="kiralandi">Kiralandı</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Tahsis Verildi seçildiğinde */}
                {formData.tahsis_verildi_mi === 'tahsis_verildi' && (
                  <>
                  <div className="space-y-2">
                    <Label className="text-white">Tahsis Edilen Kurum/Kuruluş/Tüzel Kişilik</Label>
                    <Input value={formData.tahsis_edilen_kurum || ''} onChange={(e) => setFormData({...formData, tahsis_edilen_kurum: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: İstanbul Valiliği, GSB İl Müdürlüğü vb." />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white">Tahsis Başlangıç Tarihi</Label>
                      <Input type="date" value={formData.tahsis_baslangic_tarihi} onChange={(e) => setFormData({...formData, tahsis_baslangic_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Tahsis Bitiş Tarihi</Label>
                      <Input type="date" value={formData.tahsis_bitis_tarihi} onChange={(e) => setFormData({...formData, tahsis_bitis_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                    </div>
                  </div>
                  </>
                )}

                {/* Kiraya Verildi seçildiğinde */}
                {formData.tahsis_verildi_mi === 'kiraya_verildi' && (
                  <>
                  <div className="space-y-2">
                    <Label className="text-white">Kiraya Verilen Kurum/Kuruluş/Tüzel Kişilik</Label>
                    <Input value={formData.kiraya_verilen_kurum || ''} onChange={(e) => setFormData({...formData, kiraya_verilen_kurum: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: Spor Kulübü, Dernek vb." />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white">Kira Başlangıç Tarihi</Label>
                      <Input type="date" value={formData.kira_baslangic_tarihi} onChange={(e) => setFormData({...formData, kira_baslangic_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Kira Bitiş Tarihi</Label>
                      <Input type="date" value={formData.kira_bitis_tarihi} onChange={(e) => setFormData({...formData, kira_bitis_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                    </div>
                  </div>
                  </>
                )}

                {/* Kiralandı seçildiğinde (kurumumuzun kiralayan olduğu durum) */}
                {formData.tahsis_verildi_mi === 'kiralandi' && (
                  <>
                  <div className="space-y-2">
                    <Label className="text-white">Kiralayan / Kiraya Veren Kurum/Şahıs</Label>
                    <Input value={formData.kiraya_verilen_kurum || ''} onChange={(e) => setFormData({...formData, kiraya_verilen_kurum: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: Şahıs, Kurum, Belediye vb." />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white">Kira Başlangıç Tarihi</Label>
                      <Input type="date" value={formData.kira_baslangic_tarihi} onChange={(e) => setFormData({...formData, kira_baslangic_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Kira Bitiş Tarihi</Label>
                      <Input type="date" value={formData.kira_bitis_tarihi} onChange={(e) => setFormData({...formData, kira_bitis_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Kira Bedeli (KDV Dahil) <span className="text-xs text-gray-500">(₺)</span></Label>
                    <Input type="number" step="0.01" value={formData.kira_bedeli_kdv_dahil || ''} onChange={(e) => setFormData({...formData, kira_bedeli_kdv_dahil: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: 15000.00" data-testid="kira-bedeli-kdv-input" />
                  </div>
                  <div className="mt-2 p-3 rounded-md bg-purple-900/20 border border-purple-700/40 text-xs text-purple-300">
                    💡 Bu kayıt, kaydedildiğinde otomatik olarak <strong>Kiralık Taşınmaz İşlemleri</strong> sayfasında listelenecektir.
                  </div>
                  </>
                )}

                {/* İL MÜDÜRLÜĞÜ (KAMULAŞTIRMA) seçildiğinde - Kamulaştırma bilgileri */}
                {formData.mulkiyet === 'il_mudurlugu_kamulastirma' && (
                  <div className="space-y-3 p-3 rounded-md bg-amber-900/15 border border-amber-700/40">
                    <h4 className="text-sm font-semibold text-amber-300 flex items-center gap-2">🏛️ Kamulaştırma Bilgileri</h4>
                    <div className="space-y-2">
                      <Label className="text-white">Kamulaştırma Tarihi</Label>
                      <Input type="date" value={formData.kamulastirma_tarihi || ''} onChange={(e) => setFormData({...formData, kamulastirma_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" data-testid="kamulastirma-tarihi-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Değer Tespiti Yapan Şirket</Label>
                      <Input value={formData.deger_tespit_sirketi || ''} onChange={(e) => setFormData({...formData, deger_tespit_sirketi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: ABC Değerleme A.Ş." />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white">Değer Tespit Bedeli (₺)</Label>
                        <Input type="number" step="0.01" value={formData.deger_tespit_bedeli || ''} onChange={(e) => setFormData({...formData, deger_tespit_bedeli: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white">Komisyon Tespit Bedeli (₺)</Label>
                        <Input type="number" step="0.01" value={formData.komisyon_tespit_bedeli || ''} onChange={(e) => setFormData({...formData, komisyon_tespit_bedeli: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Uzlaşma Bedeli (₺)</Label>
                      <Input type="number" step="0.01" value={formData.uzlasma_bedeli || ''} onChange={(e) => setFormData({...formData, uzlasma_bedeli: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                    </div>
                    <div className="mt-2 text-xs text-amber-300">
                      💡 Bu kayıt, kaydedildiğinde otomatik olarak <strong>Kamulaştırma İşlemleri</strong> sayfasında listelenecektir.
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-white">{['yapi', 'yapi_yikildi'].includes(formData.tasinmaz_turu) ? 'Arsa Alanı (M²)' : 'Alan (M²)'}</Label>
                  <Input type="number" value={formData.alan_m2} onChange={(e) => setFormData({...formData, alan_m2: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="Örn: 500" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Kurumumuza Tahsis Başlangıç Tarihi</Label>
                    <Input type="date" value={formData.kurum_tahsis_baslangic_tarihi} onChange={(e) => setFormData({...formData, kurum_tahsis_baslangic_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Kurumumuza Tahsis Bitiş Tarihi</Label>
                    <Input type="date" value={formData.kurum_tahsis_bitis_tarihi} onChange={(e) => setFormData({...formData, kurum_tahsis_bitis_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
                  </div>
                </div>

              </div>
            )}

            {/* Açıklama ve Özet: Arsa=3, Yapı=5 */}
            {((currentStep === 3 && !['yapi', 'yapi_yikildi'].includes(formData.tasinmaz_turu)) || (currentStep === 5 && ['yapi', 'yapi_yikildi'].includes(formData.tasinmaz_turu))) && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">📝 Açıklama ve Özet</h3>
                
                <div className="space-y-2">
                  <Label className="text-white">Açıklama</Label>
                  <Textarea
                    value={formData.aciklama}
                    onChange={(e) => setFormData({...formData, aciklama: e.target.value})}
                    className="bg-gray-800 border-gray-700 text-white min-h-24"
                    placeholder="Ek bilgiler, notlar..."
                  />
                </div>

                {/* Özet */}
                <div className="bg-gray-800 p-4 rounded-lg space-y-2">
                  <h4 className="font-semibold text-white mb-3">Kayıt Özeti:</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-400">Konum:</span> <span className="text-white ml-2">{formData.il} / {formData.ilce} / {formData.mahalle}</span></div>
                    <div><span className="text-gray-400">Kadastro:</span> <span className="text-white ml-2">Pafta: {formData.pafta || '-'}, Ada: {formData.ada}, Parsel: {formData.parsel}</span></div>
                    {(formData.parseller || []).length > 0 && (
                      <div className="col-span-2">
                        <span className="text-gray-400">Ek Parseller:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {formData.parseller.map((p, i) => (
                            <span key={i} className="text-xs bg-gray-700 text-gray-200 px-2 py-0.5 rounded">
                              {p.pafta && `P:${p.pafta}`} {p.ada && `A:${p.ada}`} {p.parsel && `Ps:${p.parsel}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div><span className="text-gray-400">Mülkiyet:</span> <span className="text-white ml-2">{formData.mulkiyet}</span></div>
                    <div><span className="text-gray-400">Alan:</span> <span className="text-white ml-2">{formData.alan_m2} M²</span></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-between">
            <div>
              {currentStep > 1 && (
                <Button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  variant="outline"
                  className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
                >
                  ← Geri
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}
                variant="outline"
                className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
              >
                İptal
              </Button>
              {(() => {
                const maxStep = ['yapi', 'yapi_yikildi'].includes(formData.tasinmaz_turu) ? 5 : 3;
                return currentStep < maxStep ? (
                  <Button
                    onClick={() => setCurrentStep(currentStep + 1)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    İleri
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="wizard-save-btn"
                  >
                    {editMode ? 'Güncelle' : 'Kaydet'}
                  </Button>
                );
              })()}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seçilileri Yazdır / Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-gray-900 border-gray-800">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" data-testid="export-dialog-title">
              Yazdır / İndir
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {exportScope === 'selected' && `${selectedRows.length} kayıt seçildi.`}
              {exportScope === 'page' && `Görünen sayfa: ${emlaklar.length} kayıt.`}
              {exportScope === 'filtered' && `Tüm filtrelenmiş: ${list.total} kayıt.`}
              {' '}Çıktıya dahil edilecek sütunları ve formatı belirleyin.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Kapsam seçimi (selected modunda gizli) */}
            {exportScope !== 'selected' && (
              <div className="space-y-2">
                <Label className="text-white font-semibold">Kapsam</Label>
                <div className="flex gap-3">
                  <Button
                    data-testid="export-scope-page"
                    onClick={() => setExportScope('page')}
                    className={exportScope === 'page'
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700'}
                  >
                    Görünen Sayfa ({emlaklar.length})
                  </Button>
                  <Button
                    data-testid="export-scope-filtered"
                    onClick={() => setExportScope('filtered')}
                    className={exportScope === 'filtered'
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700'}
                  >
                    Tüm Filtrelenmiş ({list.total})
                  </Button>
                </div>
              </div>
            )}

            {/* Format seçimi */}
            <div className="space-y-2">
              <Label className="text-white font-semibold">Çıktı Formatı</Label>
              <div className="flex gap-3">
                <Button
                  data-testid="export-format-pdf"
                  variant={exportFormat === 'pdf' ? 'default' : 'outline'}
                  onClick={() => setExportFormat('pdf')}
                  className={exportFormat === 'pdf'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'}
                >
                  PDF
                </Button>
                <Button
                  data-testid="export-format-excel"
                  variant={exportFormat === 'excel' ? 'default' : 'outline'}
                  onClick={() => setExportFormat('excel')}
                  className={exportFormat === 'excel'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'}
                >
                  Excel
                </Button>
              </div>
            </div>

            {/* Sütun seçimi */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-white font-semibold">Sütunlar</Label>
                <div className="flex gap-2">
                  <Button
                    data-testid="export-select-all-cols"
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedColumns(EXPORT_COLUMNS.map(c => c.key))}
                    className="text-blue-400 hover:text-blue-300 text-xs h-7 px-2"
                  >
                    Tümünü Seç
                  </Button>
                  <Button
                    data-testid="export-clear-all-cols"
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedColumns([])}
                    className="text-gray-400 hover:text-gray-300 text-xs h-7 px-2"
                  >
                    Temizle
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {EXPORT_COLUMNS.map(col => (
                  <label
                    key={col.key}
                    className="flex items-center gap-2 p-2 rounded-md bg-gray-800/60 hover:bg-gray-800 cursor-pointer transition-colors"
                    data-testid={`export-col-${col.key}`}
                  >
                    <Checkbox
                      checked={selectedColumns.includes(col.key)}
                      onCheckedChange={() => toggleColumn(col.key)}
                      className="border-gray-600 data-[state=checked]:bg-blue-600"
                    />
                    <span className="text-sm text-gray-200">{col.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                {selectedColumns.length} / {EXPORT_COLUMNS.length} sütun seçildi
              </p>
            </div>
          </div>

          <DialogFooter className="flex flex-wrap gap-2 pt-2 items-center">
            {exportFormat === 'pdf' && (
              <div className="flex items-center gap-1 mr-auto bg-gray-800 rounded-lg p-1">
                <span className="text-xs text-gray-400 px-2">Kağıt:</span>
                <Button size="sm" onClick={() => setPdfPageSize('a4')} className={`h-7 px-3 text-xs ${pdfPageSize === 'a4' ? 'bg-blue-600 text-white' : 'bg-transparent text-gray-400 hover:text-white'}`}>A4</Button>
                <Button size="sm" onClick={() => setPdfPageSize('a3')} className={`h-7 px-3 text-xs ${pdfPageSize === 'a3' ? 'bg-blue-600 text-white' : 'bg-transparent text-gray-400 hover:text-white'}`}>A3</Button>
              </div>
            )}
            <Button
              data-testid="export-cancel-btn"
              variant="outline"
              onClick={() => setExportDialogOpen(false)}
              className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
            >
              İptal
            </Button>
            <Button
              data-testid="export-submit-btn"
              onClick={handleExport}
              disabled={exporting || selectedColumns.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {exporting ? 'Hazırlanıyor...' : `${exportFormat === 'pdf' ? `PDF (${pdfPageSize.toUpperCase()})` : 'Excel'} İndir`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Taşınmaz Detay Dialog */}
      <EmlakDetailDialog emlak={detailEmlak} open={!!detailEmlak} onClose={() => setDetailEmlak(null)} onEdit={openEditDialog} canEdit={canEdit} />
    </div>
  );
};

export default EmlakIslemleriPage;
